import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { useAuth } from "../auth/AuthContext";
import {
  PRODUCT_LABELS,
  productsForPremise,
  type ProductKey,
} from "../nav/products";

/**
 * The app-wide viewing scope, set from the header bar and read by every page
 * that shows fleet-wide data. Empty string means "all" on each axis - a scope
 * is a narrowing, never a requirement.
 */
export interface FleetScope {
  /** Company._id, "" = every customer the user may see. */
  companyId: string;
  /** Premise slug, "" = every location in scope. */
  premiseId: string;
  /** ProductKey, "" = every product. Narrows to the places that HOLD it. */
  product: string;
}

export const EMPTY_SCOPE: FleetScope = {
  companyId: "",
  premiseId: "",
  product: "",
};

const STORAGE_KEY = "foyer.scope";

interface ScopeState {
  scope: FleetScope;
  /** Narrow one or more axes. Selecting a customer drops a foreign location. */
  setScope: (patch: Partial<FleetScope>) => void;
  /** Back to "everything". */
  clearScope: () => void;
  /** True when at least one axis is narrower than "all". */
  narrowed: boolean;
  /** Human sentence describing what is currently in view. */
  label: string;
  companies: CompanySummary[];
  premises: PremiseSummary[];
  /** The premises the current customer selection allows. */
  premisesInScope: PremiseSummary[];
  /** True once the customer/location directory has been read. */
  loaded: boolean;
  /**
   * Re-read the customer/location directory.
   *
   * The menu is built from each place's PRODUCTS, which live on these rows, so
   * anything that buys or removes a product has to say so - otherwise the
   * licence is written, the API agrees, and the nav keeps showing the old
   * shape until a full page reload. Selling a product and not seeing it appear
   * reads as a broken switch.
   */
  refreshDirectory: () => Promise<void>;
}

const ScopeCtx = createContext<ScopeState | null>(null);

function readStored(): FleetScope {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SCOPE;
    const parsed = JSON.parse(raw) as Partial<FleetScope>;
    return {
      companyId: typeof parsed.companyId === "string" ? parsed.companyId : "",
      premiseId: typeof parsed.premiseId === "string" ? parsed.premiseId : "",
      product: typeof parsed.product === "string" ? parsed.product : "",
    };
  } catch {
    return EMPTY_SCOPE;
  }
}

/**
 * Loads the directories the header selectors offer (customers, locations) and
 * holds the current selection. The selection survives reloads, but is pruned
 * against the freshly-loaded directories so a deleted premise can never keep
 * the whole CMS filtered down to nothing.
 */
export function ScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [scope, setScopeState] = useState<FleetScope>(readStored);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [loaded, setLoaded] = useState(false);

  const refreshDirectory = useCallback(async () => {
    if (!user) return;
    const [c, p] = await Promise.all([
      foyerApi.listCompanies().catch(() => [] as CompanySummary[]),
      foyerApi.listPremises().catch(() => [] as PremiseSummary[]),
    ]);
    setCompanies(c);
    setPremises(p);
    setLoaded(true);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      foyerApi.listCompanies().catch(() => [] as CompanySummary[]),
      foyerApi.listPremises().catch(() => [] as PremiseSummary[]),
    ]).then(([c, p]) => {
      if (cancelled) return;
      setCompanies(c);
      setPremises(p);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Prune a stale selection once the real directories are in.
  useEffect(() => {
    if (!loaded) return;
    setScopeState((s) => {
      const companyOk =
        !s.companyId || companies.some((c) => c.id === s.companyId);
      const premise = premises.find((p) => p.premiseId === s.premiseId);
      const premiseOk = !s.premiseId || !!premise;
      const nextCompany = companyOk ? s.companyId : "";
      // A location that belongs to another customer loses to the customer.
      const premiseFits =
        premiseOk &&
        (!nextCompany || !premise || premise.companyId === nextCompany);
      if (companyOk && premiseOk && premiseFits) return s;
      return {
        ...s,
        companyId: nextCompany,
        premiseId: premiseFits ? s.premiseId : "",
      };
    });
  }, [loaded, companies, premises]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    } catch {
      // A full/blocked storage must never break navigation.
    }
  }, [scope]);

  const setScope = useCallback(
    (patch: Partial<FleetScope>) => {
      setScopeState((s) => {
        const next = { ...s, ...patch };
        // Changing customer invalidates a location that isn't theirs.
        if (patch.companyId !== undefined && next.premiseId) {
          const p = premises.find((x) => x.premiseId === next.premiseId);
          if (next.companyId && p && p.companyId !== next.companyId) {
            next.premiseId = "";
          }
        }
        // Same for a product the selected location does not have.
        if (patch.product !== undefined && next.product && next.premiseId) {
          const p = premises.find((x) => x.premiseId === next.premiseId);
          if (p && !productsForPremise(p).has(next.product as ProductKey)) {
            next.premiseId = "";
          }
        }
        return next;
      });
    },
    [premises]
  );

  const clearScope = useCallback(() => setScopeState(EMPTY_SCOPE), []);

  const premisesInScope = useMemo(() => {
    let list = premises;
    if (scope.companyId) {
      list = list.filter((p) => p.companyId === scope.companyId);
    }
    if (scope.product) {
      // A product narrows WHICH PLACES are in play, not which devices - the
      // Location list should stop offering places that do not have it.
      list = list.filter((p) =>
        productsForPremise(p).has(scope.product as ProductKey)
      );
    }
    return list;
  }, [premises, scope.companyId, scope.product]);

  const label = useMemo(() => {
    const parts: string[] = [];
    if (scope.companyId) {
      parts.push(companies.find((c) => c.id === scope.companyId)?.name ?? "…");
    }
    if (scope.premiseId) {
      parts.push(
        premises.find((p) => p.premiseId === scope.premiseId)?.premiseName ??
          scope.premiseId
      );
    }
    if (scope.product) {
      parts.push(PRODUCT_LABELS[scope.product as ProductKey] ?? scope.product);
    }
    return parts.length === 0
      ? "Every customer, every location, every product."
      : `Showing ${parts.join(" · ")}.`;
  }, [scope, companies, premises]);

  const value = useMemo<ScopeState>(
    () => ({
      scope,
      setScope,
      clearScope,
      narrowed: !!(scope.companyId || scope.premiseId || scope.product),
      label,
      companies,
      premises,
      premisesInScope,
      loaded,
      refreshDirectory,
    }),
    [
      loaded,
      scope,
      setScope,
      clearScope,
      label,
      companies,
      premises,
      premisesInScope,
      refreshDirectory,
    ]
  );

  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

/**
 * The current scope. Safe outside the provider (tests, isolated stories):
 * falls back to "everything", so a page never has to null-check.
 */
export function useScope(): ScopeState {
  return (
    useContext(ScopeCtx) ?? {
      scope: EMPTY_SCOPE,
      setScope: () => {},
      clearScope: () => {},
      narrowed: false,
      label: "Every customer, every location, every product.",
      companies: [],
      premises: [],
      premisesInScope: [],
      loaded: true,
      // Outside the provider there is no directory to re-read.
      refreshDirectory: async () => {},
    }
  );
}
